import { monthOrder } from "@/constants/months";
import type { OpDataFieldEntry } from "@/pages/OperationalData/graphql/queries";
import type { PlanningMonth } from "../components/CostPanel/CostPanel.types";
import type { OpDataVariableOption } from "./useOpDataCatalog";

export interface MonthlyOpDataCell {
  monthKey: string; // PlanningMonth.key, "YYYY-MM"
  value: number | null; // null = figure missing in the source
}

export interface OpDataVariableCells {
  variableCode: string;
  cells: MonthlyOpDataCell[];
}

export const cellFor = (
  values: OpDataVariableCells[],
  variableCode: string,
  monthKey: string,
): number | null =>
  values
    .find((v) => v.variableCode === variableCode)
    ?.cells.find((c) => c.monthKey === monthKey)?.value ?? null;

// Every aggregate — including TOTAL — reads its stored coach-type row verbatim
// (mirrors the backend OpDataVariableResolver; TOTAL is never ERS + P2).
const monthValueFor = (
  entry: OpDataFieldEntry,
  aggregate: OpDataVariableOption["coachTypeAggregate"],
  monthEnum: (typeof monthOrder)[number],
): number | null =>
  entry.coachTypes
    .find((ct) => ct.coachType === aggregate)
    ?.months.find((m) => m.month === monthEnum)?.value ?? null;

// Per-month figures for each referenced variable. `fields` is the calculation's op
// data for its single planning year (opDataSnapshot.fields or operationalData.fields);
// every planning month resolves against it.
export const resolveOpDataVariableValues = (
  referencedOpDataVariables: OpDataVariableOption[],
  planningMonths: PlanningMonth[],
  fields: OpDataFieldEntry[],
): OpDataVariableCells[] =>
  referencedOpDataVariables.map((variable) => {
    const fieldEntry = fields.find((f) => f.field === variable.opDataField);
    return {
      variableCode: variable.code,
      cells: planningMonths.map((month) => {
        const monthNumber = Number(month.key.split("-")[1]);
        return {
          monthKey: month.key,
          value: fieldEntry
            ? monthValueFor(
                fieldEntry,
                variable.coachTypeAggregate,
                monthOrder[monthNumber - 1],
              )
            : null,
        };
      }),
    };
  });
